import { ChangeDetectionStrategy, Component, forwardRef, Inject, OnInit, Optional, SkipSelf } from '@angular/core';
import { EngineFormComponent } from '@core/engine-forms/components/engine-form/engine-form.component';
import { ENGINE_FORM_SERVICE } from '@core/engine-forms/models/iengine-form-service.model';
import { FormActions } from '@core/engine-forms/store';
import { ScreenSize } from '@core/layout/models/screen-size.enum';
import { getScreenSize } from '@core/layout/store/selectors';
import { IRecordInfo } from '@core/models/irecord-info.model';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { NavigationActions, NavigationSelectors } from '@core/navigation/store';
import { NotificationsSelectors } from '@core/notification/store';
import { ContextService } from '@core/services/context.service';
import { NoEventWidgetDirective } from '@core/widgets/directives/no-event-widget.directive';
import { WidgetDirective } from '@core/widgets/directives/widget.directive';
import { IScriptRunnerService, SCRIPT_RUNNER_SERVICE } from '@core/widgets/models/iscript-runner.service';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SavingExecutionEvent } from '../../core/execution-context';
import { EventType } from '../../core/services/api-clients';
import { NotificationGroup, NotificationScope } from '../../engine-sdk';
import { IFormArgs } from './main-form.model';
import { IFormServiceState, MainFormService } from './main-form.service';

@Component({
  selector: 'app-main-form',
  templateUrl: './main-form.component.html',
  styleUrls: ['./main-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: ENGINE_FORM_SERVICE, useClass: MainFormService },
    {
      provide: WidgetDirective,
      useExisting: forwardRef(() => MainFormComponent),
    },
  ],
})
export class MainFormComponent extends NoEventWidgetDirective implements OnInit {
  formData$: Observable<IFormServiceState> = this._formService.getFormStateAsync();
  notificationGroups$: Observable<NotificationGroup[]>;
  isCompactMode$ = this._store.select(getScreenSize).pipe(map((screenSize) => screenSize <= ScreenSize.MD));

  constructor(
    @Optional() @SkipSelf() parentWidget: WidgetDirective,
    @Inject(SCRIPT_RUNNER_SERVICE) scriptRunnerService: IScriptRunnerService,
    private _store: Store,
    private _context: ContextService,
    @Inject(ENGINE_FORM_SERVICE) private _formService: MainFormService,
    private _navigationService: NavigationService,
  ) {
    super(parentWidget, scriptRunnerService);
    this._minWidgetsNumber = 3; // contextMenu + entityForm + asyncWidget
  }

  ngOnInit(): void {
    this._store.dispatch(new FormActions.LoadCurrentFormDto());

    this.getRecordInfoAsync()
      .pipe(
        first(),
        tap((recordInfo) => {
          this._formService.init(recordInfo);
        }),
      )
      .subscribe();

    this.notificationGroups$ = combineLatest([
      this.getRecordInfoAsync(),
      this._store.select(NotificationsSelectors.getNotificationGroups),
    ]).pipe(
      filter(([recordInfo, _]) => !!recordInfo),
      map(([recordInfo, notificationGroups]) =>
        Object.values(notificationGroups).filter(
          (x) => x.scope === NotificationScope.Attribute && recordInfo.entityName === x.entityName,
        ),
      ),
    );

    // TODO-MP: trzeba się tego pozbyć (o ile się da - sdk)
    this._context
      .getMainFormContextAsync()
      .pipe(
        takeUntil(this._destroy$),
        filter((x) => !!x),
        switchMap((x: EngineFormComponent) => x.events$.pipe(
          takeUntil(this._destroy$),
          filter((e) => e.eventType === EventType.OnSaving),
          map((e) => e as SavingExecutionEvent),
        )),
        tap((s) => s.saveAsync()),
      )
      .subscribe();
  }

  onFormInitiated(form: EngineFormComponent, args: IFormArgs) {
    if (!args) return;
    if (args.$markAsDirty) {
      Object.keys(args)
        .filter((arg) => arg != '$markAsDirty' && arg != '$mappings')
        .forEach((arg) => form.setFieldValue(arg, args[arg]));
    }
  }

  onIsDirtyChanged(isDirty: boolean) {
    this._store.dispatch(new NavigationActions.SetIsDirty({ isDirty }));
  }

  onSelectedTabChange(index: number) {
    this._navigationService.addQueryParams({ tabIndex: index });
  }

  private getRecordInfoAsync(): Observable<IRecordInfo> {
    return this._store.select(NavigationSelectors.getCurrentNavigationTargets).pipe(
      takeUntil(this._destroy$),
      filter((targets) => !!targets.length),
      map((targets) => targets.slice(-1)[0]),
      map((currentNavigationTarget) => {
        return {
          entityId: currentNavigationTarget.entityId,
          entityName: currentNavigationTarget.entityName,
          recordId: currentNavigationTarget.recordId,
        };
      }),
      first(),
    );
  }
}
